import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import FloatingCTA from '@/components/feature/FloatingCTA';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import SEO from '@/components/feature/SEO';

interface Product {
  id: string;
  category: string;
  categoryName?: string;
  title: string;
  desc: string;
  price: string;
  image: string;
  cta: string;
  link: string;
  badge?: string;
}

const ProductsPage = () => {
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<{ key: string; label: string; icon: string }[]>([
    { key: 'all', label: t('products.categories.all'), icon: 'ri-apps-2-line' }
  ]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const prodSnap = await getDocs(collection(db, 'products'));
        const catSnap = await getDocs(collection(db, 'categories'));

        const fetchedProducts = prodSnap.docs.map((d) => ({ id: d.id, ...d.data() })) as Product[];
        const fetchedCats = catSnap.docs.map((d) => {
          const data = d.data();
          return { key: d.id, label: data.name, icon: 'ri-folder-2-line' };
        });

        setProducts(fetchedProducts);
        
        setCategories([
          { key: 'all', label: t('products.categories.all'), icon: 'ri-apps-2-line' },
          ...fetchedCats
        ]);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [t]);

  const filtered = activeCategory === 'all'
    ? products
    : products.filter((p) => p.category === activeCategory);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030006] flex items-center justify-center">
        <i className="ri-loader-4-line text-[#8c52ff] text-4xl animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030006] text-white">
      <SEO 
        title="Cursos y Productos Digitales | Sbdigisolutions" 
        description="Explora nuestros cursos, herramientas digitales, talleres y consultorías diseñadas para optimizar tu presencia digital." 
      />
      {/* Hero */}
      <section className="relative pt-36 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_20%,rgba(140,82,255,0.18)_0%,transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_80%,rgba(0,194,255,0.10)_0%,transparent_60%)]" />
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center relative z-10">
          <p className="text-[#FF2DAA] text-xs font-bold uppercase tracking-widest mb-4">{t('products.hero.badge')}</p>
          <h1 className="text-4xl md:text-6xl font-black mb-6 leading-tight">
            {t('products.hero.titlePrefix')}{' '}
            <span className="bg-gradient-to-r from-[#8c52ff] via-[#FF2DAA] to-[#00c2ff] bg-clip-text text-transparent">
              {t('products.hero.titleHighlight')}
            </span>
          </h1>
          <p className="text-white/60 text-lg max-w-2xl mx-auto leading-relaxed">
            {t('products.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="sticky top-20 z-40 bg-[#030006]/90 backdrop-blur-xl border-b border-white/8 py-4">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {categories.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`whitespace-nowrap flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 cursor-pointer flex-shrink-0 ${
                  activeCategory === cat.key
                    ? 'bg-gradient-to-r from-[#8c52ff] to-[#FF2DAA] text-white'
                    : 'bg-white/5 border border-white/10 text-white/60 hover:text-white hover:border-white/30'
                }`}
              >
                <i className={`${cat.icon} text-base`} />
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <p className="text-white/30 text-sm mb-8">
            {filtered.length} {filtered.length === 1 ? t('products.grid.foundSingle') : t('products.grid.foundPlural')}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filtered.map((product) => (
              <div
                key={product.id}
                className="group relative bg-white/3 border border-white/8 rounded-2xl overflow-hidden hover:border-[#8c52ff]/40 transition-all duration-500 hover:-translate-y-2 flex flex-col"
              >
                {product.badge && (
                  <div className="absolute top-3 left-3 z-10 bg-gradient-to-r from-[#8c52ff] to-[#FF2DAA] text-white text-xs font-bold px-3 py-1 rounded-full">
                    {product.badge}
                  </div>
                )}
                <div className="relative h-48 overflow-hidden flex-shrink-0">
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#030006] via-transparent to-transparent" />
                  <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm border border-white/10 rounded-full px-3 py-1">
                    <span className="text-[#00c2ff] font-black text-sm">{product.price}</span>
                  </div>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <div className="mb-1">
                    <span className="text-[#8c52ff] text-xs font-semibold uppercase tracking-wider">
                      {categories.find((c) => c.key === product.category)?.label}
                    </span>
                  </div>
                  <h3 className="text-white font-bold text-base leading-tight mb-2">{product.title}</h3>
                  <p className="text-white/50 text-xs leading-relaxed mb-5 flex-1">{product.desc}</p>
                  <a
                    href={product.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="whitespace-nowrap w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#8c52ff] to-[#00c2ff] text-white text-sm font-semibold py-2.5 rounded-xl hover:opacity-90 transition-opacity duration-300 cursor-pointer"
                  >
                    {product.cta}
                    <i className="ri-external-link-line text-xs" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#8c52ff]/10 via-transparent to-[#00c2ff]/10" />
        <div className="max-w-3xl mx-auto px-4 md:px-8 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
            {t('products.footerCta.title')}
          </h2>
          <p className="text-white/60 mb-8 text-lg">
            {t('products.footerCta.subtitle')}
          </p>
          <a
            href="https://stan.store"
            target="_blank"
            rel="noopener noreferrer"
            className="whitespace-nowrap inline-flex items-center gap-2 bg-gradient-to-r from-[#8c52ff] to-[#FF2DAA] text-white font-bold px-8 py-4 rounded-full hover:opacity-90 transition-opacity duration-300 cursor-pointer text-base"
          >
            <i className="ri-calendar-line" />
            {t('products.footerCta.cta')}
          </a>
        </div>
      </section>
    </div>
  );
};

export default ProductsPage;
